/**
 * File: documentsService.ts
 * Path: apps/admin/src/shared/services/documentsService.ts
 * Description: Files attached to a business. New files go to
 * businesses/{folder}/documents/ with an index entry at
 * businesses/{id}/documents/{docId}; files still in the old
 * documents/<leads|clients|jobs>/<id>/ folders are listed too (union, no
 * duplicates) and a record that is not a business yet keeps using them.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import {
  auth,
  BUSINESSES_COLLECTION,
  businessDocumentPath,
  db,
  documentFolder,
  documentPath,
} from "@idk/config";
import { StorageService } from "./storageService";
import {
  displayName,
  documentKey,
  mergeDocuments,
  type AttachedDocument,
  type DocumentFolder,
} from "../utils/documentsUtils";

export type { AttachedDocument, DocumentFolder };

const INDEX = "documents";

interface BusinessHome {
  id: string;
  /** Storage folder name; absent until the business has been moved. */
  folder: string | null;
}

/** The business a lead/client id belongs to (its own id, or via legacy ids), else null. */
const findBusiness = async (owner: DocumentFolder): Promise<BusinessHome | null> => {
  if (owner.kind === "jobs") return null;
  const asHome = (id: string, data: Record<string, unknown>): BusinessHome => ({
    id,
    folder: typeof data.folder === "string" && data.folder ? data.folder : null,
  });
  const direct = await getDoc(doc(db, BUSINESSES_COLLECTION, owner.id));
  if (direct.exists()) return asHome(direct.id, direct.data());
  const field = owner.kind === "clients" ? "legacy.clientId" : "legacy.leadId";
  const hit = await getDocs(
    query(collection(db, BUSINESSES_COLLECTION), where(field, "==", owner.id), limit(1)),
  );
  const first = hit.docs[0];
  return first ? asHome(first.id, first.data()) : null;
};

const indexCollection = (businessId: string) =>
  collection(db, BUSINESSES_COLLECTION, businessId, INDEX);

/** Same id for the same file path, so a repeated write replaces rather than duplicates. */
const indexIdOf = (path: string): string => path.split("/").pop()?.replace(/[^\w.-]/g, "_") ?? path;

const toDocument = (
  file: Awaited<ReturnType<typeof StorageService.listDetailed>>[number],
  owner: DocumentFolder,
): AttachedDocument => ({
  path: file.path,
  name: displayName(file.name, file.customMetadata.originalName),
  url: file.url,
  size: file.size,
  uploadedAt: file.updated,
  tag: file.customMetadata.tag ?? "",
  owner,
});

const listIndexed = async (
  home: BusinessHome,
  owner: DocumentFolder,
): Promise<AttachedDocument[]> => {
  const snap = await getDocs(indexCollection(home.id));
  const rows = await Promise.all(
    snap.docs.map(async (entry): Promise<AttachedDocument | null> => {
      const d = entry.data();
      const path = typeof d.path === "string" ? d.path : "";
      if (!path) return null;
      try {
        return {
          path,
          name: typeof d.name === "string" ? d.name : displayName(path.split("/").pop() ?? path, undefined),
          url: await StorageService.urlFor(path),
          size: typeof d.size === "number" ? d.size : 0,
          uploadedAt: typeof d.uploadedAt === "number" ? d.uploadedAt : 0,
          tag: typeof d.tag === "string" ? d.tag : "",
          owner,
          index: { businessId: home.id, indexId: entry.id },
        };
      } catch {
        return null; // index entry whose file is gone
      }
    }),
  );
  return rows.filter((r): r is AttachedDocument => r !== null);
};

const listLegacy = async (owner: DocumentFolder): Promise<AttachedDocument[]> =>
  (await StorageService.listDetailed(documentFolder(owner.kind, owner.id))).map((file) =>
    toDocument(file, owner),
  );

/** Plain-words failure from remove, safe to show on screen. */
export class DocumentRemoveError extends Error {}

const isMissing = (error: unknown): boolean =>
  typeof error === "object" && error !== null && (error as { code?: string }).code === "storage/object-not-found";

export const documentsRepo = {
  /** Every file for the given records: the business index plus old-folder files not yet indexed. */
  list: async (folders: readonly DocumentFolder[]): Promise<AttachedDocument[]> => {
    const perFolder = await Promise.all(
      folders.map(async (owner) => {
        const home = await findBusiness(owner);
        const [indexed, legacy] = await Promise.all([
          home ? listIndexed(home, owner) : Promise.resolve([]),
          listLegacy(owner),
        ]);
        return mergeDocuments(indexed, legacy);
      }),
    );
    // Lead and client of one business share an index: merge across folders too.
    return perFolder.reduce<AttachedDocument[]>((all, next) => mergeDocuments(all, next), []);
  },

  upload: async (owner: DocumentFolder, file: File, tag = ""): Promise<AttachedDocument> => {
    const home = await findBusiness(owner);
    const now = Date.now();
    const path = home?.folder
      ? businessDocumentPath(home.folder, file.name, now)
      : documentPath(owner.kind, owner.id, file.name, now);
    const stored = await StorageService.putFile({
      path,
      file,
      metadata: {
        contentType: file.type,
        customMetadata: { originalName: file.name, ...(tag ? { tag } : {}) },
      },
    });
    const result = toDocument(stored, owner);
    if (home?.folder) {
      const indexId = indexIdOf(path);
      await setDoc(doc(db, BUSINESSES_COLLECTION, home.id, INDEX, indexId), {
        path,
        name: file.name,
        tag,
        size: stored.size,
        uploadedAt: stored.updated,
        uploadedBy: auth?.currentUser?.email ?? "",
      });
      result.index = { businessId: home.id, indexId };
    }
    return result;
  },

  /**
   * Removes every copy of a document: its file, its index entry, and any
   * old-folder file with the same name and size (the one the list would show
   * again). Tries all of them, then reports plainly what could not be removed.
   */
  remove: async (
    target: Pick<AttachedDocument, "path" | "index"> & Partial<Pick<AttachedDocument, "name" | "size" | "owner">>,
    folders: readonly DocumentFolder[] = target.owner ? [target.owner] : [],
  ): Promise<void> => {
    const problems: string[] = [];
    const attempt = async (what: string, run: () => Promise<unknown>) => {
      try {
        await run();
      } catch (error) {
        if (!isMissing(error)) problems.push(what);
      }
    };
    await attempt("the file", () => StorageService.deleteFile(target.path));
    const index = target.index;
    if (index) {
      await attempt("its list entry", () =>
        deleteDoc(doc(db, BUSINESSES_COLLECTION, index.businessId, INDEX, index.indexId)),
      );
    }
    if (index && target.name !== undefined && target.size !== undefined) {
      const key = documentKey({ name: target.name, size: target.size });
      const olds = (await Promise.all(folders.map((f) => listLegacy(f).catch(() => []))))
        .flat()
        .filter((d) => documentKey(d) === key && d.path !== target.path);
      for (const old of olds) {
        await attempt("the old copy (this account is not allowed to delete it)", () =>
          StorageService.deleteFile(old.path),
        );
      }
    }
    if (problems.length) {
      throw new DocumentRemoveError(`Could not remove ${problems.join(" and ")}. Ask Liam's developer to remove it.`);
    }
  },

  /** Deletes every file attached to one record (used by "Remove this person's details"). */
  removeAll: async (owner: DocumentFolder): Promise<number> => {
    const home = await findBusiness(owner);
    const indexed = home ? await listIndexed(home, owner) : [];
    const legacy = await listLegacy(owner);
    await Promise.all([
      ...indexed.map((d) => documentsRepo.remove(d)),
      ...legacy.map((d) => StorageService.deleteFile(d.path)),
    ]);
    return indexed.length + legacy.length;
  },
};
